/**
 * @fileoverview Hook Data Layer to load call events for a customer in Inbox.
 * Visibility by agent is enforced server-side via CallEvents::ListByRetailer.
 * @module useCustomerCallsData
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useDispatch } from 'react-redux';
import CallEventsMC from '../../../models/CallEventsMC';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { CUSTOMER_CALLS_PAGE_SIZE } from '../../customers/constants/customerCallsConstants';

/**
 * @param {Object|null|undefined} customer
 */
export default function useCustomerCallsData(customer) {
  const dispatch = useDispatch();
  const customerId = customer?.id;
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchParams, setFetchParams] = useState({
    customerId: null,
    page: 1,
    revision: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false
  });

  useEffect(() => {
    setFetchParams({
      customerId: customerId ?? null,
      page: 1,
      revision: 0
    });
    setCalls([]);
    setError(null);
    setPagination({
      currentPage: 1,
      totalPages: 1,
      hasNextPage: false
    });
  }, [customerId]);

  useEffect(() => {
    const { customerId: id, page } = fetchParams;
    if (!id) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    CallEventsMC.get({
      customer_id: id,
      page,
      per_page: CUSTOMER_CALLS_PAGE_SIZE
    })
      .then((data) => {
        if (cancelled) return;

        const nextCalls = data?.call_events || [];
        const totalPages = data?.pages || 1;

        setCalls((prevCalls) => (
          page === 1 ? nextCalls : [...prevCalls, ...nextCalls]
        ));
        setPagination({
          currentPage: page,
          totalPages,
          hasNextPage: page < totalPages
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;

        const message = err?.message || 'CUSTOMER_CALLS_LOAD_FAILED';
        setError(message);
        dispatch({
          type: LOAD_DATA_FAILURE,
          error: message
        });
        if (page === 1) {
          setCalls([]);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            hasNextPage: false
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchParams, dispatch]);

  const loadMoreCalls = useCallback(() => {
    if (!pagination.hasNextPage || loading) return;
    setFetchParams((prev) => ({
      ...prev,
      page: prev.page + 1
    }));
  }, [pagination.hasNextPage, loading]);

  const reload = useCallback(() => {
    if (!customerId) return;
    setError(null);
    setFetchParams((prev) => ({
      customerId,
      page: 1,
      revision: prev.revision + 1
    }));
  }, [customerId]);

  return useMemo(() => ({
    calls,
    loading,
    error,
    pagination,
    loadMoreCalls,
    reload
  }), [calls, loading, error, pagination, loadMoreCalls, reload]);
}
